import { Controller } from "@hotwired/stimulus"

// Handles toggle switches (topbar, sidebar) and color theme (save/reset)
// for the Appearance admin section.
//
// Usage:
//   <div data-controller="manage--appearance"
//        data-manage--appearance-url-value="/manage/slug/admin/appearance">
//
//   Toggle:
//     <input type="checkbox" data-action="change->manage--appearance#toggle"
//            data-section="topbar" data-key="value">
//
//   Color picker:
//     <input type="color" data-action="input->manage--appearance#updateColor"
//            data-manage--appearance-target="mainColor">
//
//   Save theme:
//     <button data-action="click->manage--appearance#saveTheme">
//
//   Reset theme:
//     <button data-action="click->manage--appearance#resetTheme">
export default class extends Controller {
  static targets = ["mainColor", "navColor", "mainPreview", "navPreview", "saveBtn"]
  static values = {
    url: String,
    defaultMainColor: { type: String, default: "#000000" },
    defaultNavColor: { type: String, default: "#f4f4f4" },
    originalMainColor: String,
    originalNavColor: String
  }

  connect() {
    if (this.hasMainColorTarget) {
      this.originalMainColorValue = this.mainColorTarget.value
    }
    if (this.hasNavColorTarget) {
      this.originalNavColorValue = this.navColorTarget.value
    }
  }

  // Toggle switch → immediate PATCH
  toggle(event) {
    const checkbox = event.currentTarget
    const section = checkbox.dataset.section
    const value = checkbox.checked

    this.#patch({ section, value: String(value) })
  }

  // Update color preview circle on input change
  updateColor(event) {
    const input = event.currentTarget
    const target = input.dataset.preview

    if (target === "main" && this.hasMainPreviewTarget) {
      this.mainPreviewTarget.style.backgroundColor = input.value
    } else if (target === "nav" && this.hasNavPreviewTarget) {
      this.navPreviewTarget.style.backgroundColor = input.value
    }

    this.#updateSaveButtonState()
  }

  // Save theme colors
  saveTheme(event) {
    event.preventDefault()
    if (!this.hasMainColorTarget || !this.hasNavColorTarget) return

    const btn = event.currentTarget
    btn.disabled = true

    this.#patch({
      section: "theme",
      main_color: this.mainColorTarget.value,
      nav_color: this.navColorTarget.value
    }).then(() => {
      this.originalMainColorValue = this.mainColorTarget.value
      this.originalNavColorValue = this.navColorTarget.value
      this.#updateSaveButtonState()
      btn.disabled = false
    }).catch(() => {
      btn.disabled = false
    })
  }

  // Reset theme to defaults
  resetTheme(event) {
    event.preventDefault()

    if (this.hasMainColorTarget) {
      this.mainColorTarget.value = this.defaultMainColorValue
    }
    if (this.hasNavColorTarget) {
      this.navColorTarget.value = this.defaultNavColorValue
    }
    if (this.hasMainPreviewTarget) {
      this.mainPreviewTarget.style.backgroundColor = this.defaultMainColorValue
    }
    if (this.hasNavPreviewTarget) {
      this.navPreviewTarget.style.backgroundColor = this.defaultNavColorValue
    }

    this.#patch({ section: "reset_theme" }).then(() => {
      this.originalMainColorValue = this.defaultMainColorValue
      this.originalNavColorValue = this.defaultNavColorValue
      this.#updateSaveButtonState()
    })
  }

  // Pick a preset color
  pickPreset(event) {
    event.preventDefault()
    const color = event.currentTarget.dataset.color
    const target = event.currentTarget.dataset.colorTarget

    if (target === "main" && this.hasMainColorTarget) {
      this.mainColorTarget.value = color
      if (this.hasMainPreviewTarget) this.mainPreviewTarget.style.backgroundColor = color
    } else if (target === "nav" && this.hasNavColorTarget) {
      this.navColorTarget.value = color
      if (this.hasNavPreviewTarget) this.navPreviewTarget.style.backgroundColor = color
    }

    this.#updateSaveButtonState()
  }

  // Private

  #updateSaveButtonState() {
    if (!this.hasSaveBtnTarget) return

    const colorsChanged = (this.hasMainColorTarget && this.mainColorTarget.value !== this.originalMainColorValue) ||
                          (this.hasNavColorTarget && this.navColorTarget.value !== this.originalNavColorValue)

    this.saveBtnTarget.disabled = !colorsChanged
  }

  #patch(data) {
    return fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.#csrfToken()
      },
      body: JSON.stringify(data)
    }).then(response => {
      if (response.ok) {
        return response.text().then(html => {
          if (html.trim()) Turbo.renderStreamMessage(html)
        })
      }
    })
  }

  #csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
